/**
 * Runtime config. babel-preset-expo doesn't inline `process.env.EXPO_PUBLIC_*`
 * inside node_modules — consumers pass values via <LoginRoot config={...} />;
 * standalone SPA falls through to env vars. Required getters throw on
 * missing; optional getters return undefined.
 */

// Flag key constants — public: hosts import these to key their config.featureFlags
// map and call their own useFeatureFlag(...) hook, so no PostHog string is
// duplicated across the package boundary.
export const BIOMETRIC_FLAG = 'biometric-login' as const;
export const DESTINATION_MEMBER_FLAG = 'login-destination-member' as const;
export const DESTINATION_ONBOARDING_FLAG = 'login-destination-onboarding' as const;
export const DESTINATION_DELPHI_FLAG = 'login-destination-delphi' as const;
export const DESTINATION_HERMES_FLAG = 'login-destination-hermes' as const;
// Same flag VRN uses to switch its kit to @vidahealth/vida-ds.
export const DESIGN_SYSTEM_FLAG = 'rn-vida-ds' as const;

export const ONBOARDING_RN_FLAG = 'login-destination-onboarding-react-native' as const;
export const ONBOARDING_RN_VARIANT = 'test' as const;

// Adding a key here widens LoginFeatureFlagKey → LoginFeatureFlags → breaks
// every embedded consumer's config at compile time. That's the forcing function.
export const LOGIN_FEATURE_FLAG_KEYS = [
  BIOMETRIC_FLAG,
  DESTINATION_MEMBER_FLAG,
  DESTINATION_ONBOARDING_FLAG,
  DESTINATION_DELPHI_FLAG,
  DESTINATION_HERMES_FLAG,
  DESIGN_SYSTEM_FLAG,
] as const;

export const LOGIN_VARIANT_FLAG_KEYS = [ONBOARDING_RN_FLAG] as const;

export type LoginFeatureFlagKey = (typeof LOGIN_FEATURE_FLAG_KEYS)[number];
export type LoginVariantFlagKey = (typeof LOGIN_VARIANT_FLAG_KEYS)[number];
export type LoginFeatureFlags = { [K in LoginFeatureFlagKey]: boolean };
// Value may be undefined (no override → fall through to PostHog); the key
// must be present under embed so the host acknowledges each variant flag.
export type LoginVariantFlags = { [K in LoginVariantFlagKey]: string | undefined };

type CoreLoginConfig = {
  apiBaseUrl?: string;
  vidaWebUrl?: string;
  zendeskBaseUrl?: string;
  supportUrl?: string;
  supportEmail?: string;
  ssoCoachDashboardUrl?: string;
  ssoHealthGuideUrl?: string;
  ssoZendeskUrl?: string;
  ssoClientPortalUrl?: string;
  // Full URL for RN onboarding (e.g. https://app.vida.com/onboarding/) —
  // required off the spa-router origin (VRN native, local dev).
  onboardingUrl?: string;
  skipLegacySession?: boolean;
  vidaEnv?: string;
  buildBranch?: string;
  buildCommit?: string;
  buildTime?: string;
  transifex?: {
    token?: string;
  };
  posthog?: {
    enabled?: boolean;
    apiKey?: string;
    host?: string;
  };
  datadog?: {
    enabled?: boolean;
    applicationId?: string;
    clientToken?: string;
    sessionReplayEnabled?: boolean;
  };
};

// Discriminated by `embedded`: under `embedded: true` the host owns analytics
// and MUST provide every registered flag; standalone leaves both optional.
// The two branches are exported by name so hosts that keep their config in a
// typed variable can annotate it directly — a `LoginConfig` union value isn't
// assignable to `LoginRoot`'s discriminated `config` prop without narrowing.
export type StandaloneLoginConfig = CoreLoginConfig & {
  embedded?: false;
  featureFlags?: Partial<LoginFeatureFlags>;
  variantFlags?: Partial<LoginVariantFlags>;
};

export type EmbeddedLoginConfig = CoreLoginConfig & {
  embedded: true;
  featureFlags: LoginFeatureFlags;
  variantFlags: LoginVariantFlags;
};

export type LoginConfig = StandaloneLoginConfig | EmbeddedLoginConfig;

let current: CoreLoginConfig & {
  embedded?: boolean;
  featureFlags?: Partial<LoginFeatureFlags>;
  variantFlags?: Partial<LoginVariantFlags>;
} = {};

export const setRuntimeConfig = (config: LoginConfig): void => {
  current = { ...current, ...config };
};

const missing = (name: string): never => {
  throw new Error(
    `@vidahealth/login: ${name} is not configured. Pass it via <LoginRoot config={...} /> or the matching EXPO_PUBLIC_ env var.`
  );
};

const requireString = (name: string, value: string | undefined): string =>
  value ?? missing(name);

const requireBool = (
  name: string,
  runtime: boolean | undefined,
  envRaw: string | undefined
): boolean => {
  if (runtime !== undefined) return runtime;
  if (envRaw !== undefined) return envRaw === 'true';
  return missing(name);
};

export const getApiBaseUrl = () =>
  requireString('apiBaseUrl', current.apiBaseUrl ?? process.env.EXPO_PUBLIC_VIDA_API_URL);

export const getVidaWebUrl = () =>
  requireString('vidaWebUrl', current.vidaWebUrl ?? process.env.EXPO_PUBLIC_VIDA_WEB_URL);

export const getZendeskBaseUrl = () =>
  requireString(
    'zendeskBaseUrl',
    current.zendeskBaseUrl ?? process.env.EXPO_PUBLIC_ZENDESK_BASE_URL
  );

export const getSupportUrl = () =>
  requireString('supportUrl', current.supportUrl ?? process.env.EXPO_PUBLIC_SUPPORT_URL);

export const getSupportEmail = () =>
  requireString('supportEmail', current.supportEmail ?? process.env.EXPO_PUBLIC_SUPPORT_EMAIL);

export const getVidaEnv = () =>
  requireString('vidaEnv', current.vidaEnv ?? process.env.EXPO_PUBLIC_VIDA_ENV);

// Optional — undefined = "not applicable" (non-member SSO for VRN,
// build tags on dev machines).
export const getSsoCoachDashboardUrl = (): string | undefined =>
  current.ssoCoachDashboardUrl ?? process.env.EXPO_PUBLIC_SSO_COACH_DASHBOARD_URL;

// Undefined → callers fall back to ONBOARDING_RN_PATH (relative, spa-router only).
export const getOnboardingUrl = (): string | undefined =>
  current.onboardingUrl ?? process.env.EXPO_PUBLIC_ONBOARDING_URL;

export const getSsoHealthGuideUrl = (): string | undefined =>
  current.ssoHealthGuideUrl ?? process.env.EXPO_PUBLIC_SSO_HEALTH_GUIDE_URL;

// Okta Zendesk app — staff-only. Unset hides the employee option on sso-login.
export const getSsoZendeskUrl = (): string | undefined =>
  current.ssoZendeskUrl ?? process.env.EXPO_PUBLIC_SSO_ZENDESK_URL;

// Okta Client Command Center app — staff-only. Unset keeps /client-portal on the org picker.
export const getSsoClientPortalUrl = (): string | undefined =>
  current.ssoClientPortalUrl ?? process.env.EXPO_PUBLIC_SSO_CLIENT_PORTAL_URL;

export const getBuildBranch = (): string | undefined =>
  current.buildBranch ?? process.env.EXPO_PUBLIC_BUILD_BRANCH;

export const getBuildCommit = (): string | undefined =>
  current.buildCommit ?? process.env.EXPO_PUBLIC_BUILD_COMMIT;

export const getBuildTime = (): string | undefined =>
  current.buildTime ?? process.env.EXPO_PUBLIC_BUILD_TIME;

export const getTransifexToken = () =>
  requireString(
    'transifex.token',
    current.transifex?.token ?? process.env.EXPO_PUBLIC_LOGIN_TRANSIFEX_TOKEN
  );

export const getPostHogApiKey = () =>
  requireString(
    'posthog.apiKey',
    current.posthog?.apiKey ?? process.env.EXPO_PUBLIC_POSTHOG_API_KEY
  );

export const getPostHogHost = () =>
  requireString(
    'posthog.host',
    current.posthog?.host ?? process.env.EXPO_PUBLIC_POSTHOG_HOST
  );

export const getDatadogApplicationId = () =>
  requireString(
    'datadog.applicationId',
    current.datadog?.applicationId ?? process.env.EXPO_PUBLIC_DATADOG_APPLICATION_ID
  );

export const getDatadogClientToken = () =>
  requireString(
    'datadog.clientToken',
    current.datadog?.clientToken ?? process.env.EXPO_PUBLIC_DATADOG_CLIENT_TOKEN
  );

export const getSkipLegacySession = () =>
  requireBool(
    'skipLegacySession',
    current.skipLegacySession,
    process.env.EXPO_PUBLIC_SKIP_LEGACY_SESSION
  );

export const getPostHogEnabled = () =>
  requireBool('posthog.enabled', current.posthog?.enabled, process.env.EXPO_PUBLIC_POSTHOG_ENABLED);

export const getDatadogEnabled = () =>
  requireBool('datadog.enabled', current.datadog?.enabled, process.env.EXPO_PUBLIC_DATADOG_ENABLED);

export const getDatadogSessionReplayEnabled = () =>
  requireBool(
    'datadog.sessionReplayEnabled',
    current.datadog?.sessionReplayEnabled,
    process.env.EXPO_PUBLIC_DATADOG_SESSION_REPLAY_ENABLED
  );

export const isEmbedded = (): boolean => current.embedded === true;

export const getConfigFeatureFlag = (
  key: keyof LoginFeatureFlags
): boolean | undefined => current.featureFlags?.[key];

export const getConfigVariantFlag = (
  key: keyof LoginVariantFlags
): string | undefined => current.variantFlags?.[key];
